// Hard water checker: city selector, postal code lookup and water system tabs.
// All data and copy are server-rendered by sections/hard-water-checker.liquid; this file only toggles it.
(() => {
  const POSTAL = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z](\d[ABCEGHJ-NPRSTV-Z]\d)?$/;

  function init(root) {
    if (root.dataset.hwReady) return;
    root.dataset.hwReady = 'true';

    const form = root.querySelector('[data-hw-form]');
    const select = root.querySelector('[data-hw-select]');
    const postal = root.querySelector('[data-hw-postal]');
    const msg = root.querySelector('[data-hw-msg]');
    const prompt = root.querySelector('[data-hw-prompt]');
    const panels = [...root.querySelectorAll('[data-hw-panel]')];
    const recs = root.querySelector('[data-hw-recs]');
    const cities = [...select.options].filter((o) => o.value);

    const say = (text) => {
      msg.textContent = text || '';
    };

    function showRating(rating) {
      if (!recs) return;
      recs.hidden = !rating;
      recs.querySelectorAll('[data-hw-products]').forEach((el) => (el.hidden = el.dataset.hwProducts !== rating));
      recs.querySelectorAll('[data-hw-advice]').forEach((el) => (el.hidden = el.dataset.hwAdvice !== rating));
    }

    function activateSystem(panel, id) {
      panel.querySelectorAll('[data-hw-system]').forEach((el) => (el.hidden = el.dataset.hwSystem !== id));
      panel.querySelectorAll('[data-hw-tab]').forEach((el) => el.setAttribute('aria-pressed', String(el.dataset.hwTab === id)));
      showRating(panel.querySelector(`[data-hw-system="${id}"]`)?.dataset.rating);
    }

    function show(id) {
      let active = null;
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.hwPanel !== id;
        if (!panel.hidden) active = panel;
      });
      prompt.hidden = Boolean(active);
      if (active) {
        const current = active.querySelector('[data-hw-system]:not([hidden])') || active.querySelector('[data-hw-system]');
        activateSystem(active, current.dataset.hwSystem);
      } else {
        showRating(null);
      }
      const url = new URL(window.location.href);
      if (id) url.searchParams.set('city', id);
      else url.searchParams.delete('city');
      window.history.replaceState(window.history.state, '', url);
    }

    function cityForPostal(code) {
      const fsa = code.slice(0, 3);
      return cities.find((option) => {
        const list = (attr) => (option.dataset[attr] || '').split(',').filter(Boolean);
        return list('fsa').some((p) => fsa.startsWith(p)) && !list('exclude').some((p) => fsa.startsWith(p));
      });
    }

    function lookup(raw) {
      const code = raw.toUpperCase().replace(/\s+/g, '');
      if (!POSTAL.test(code)) {
        say(root.dataset.msgInvalid);
        return;
      }
      const fsa = code.slice(0, 3);
      const match = cityForPostal(code);
      if (match) {
        select.value = match.value;
        say(root.dataset.msgMatch.replace('__FSA__', fsa).replace('__CITY__', match.textContent.trim()));
        show(match.value);
      } else {
        select.value = '';
        say(root.dataset.msgNone.replace('__FSA__', fsa));
        show('');
      }
    }

    select.addEventListener('change', () => {
      say('');
      postal.value = '';
      show(select.value);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      lookup(postal.value);
    });

    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-hw-tab]');
      if (!tab) return;
      activateSystem(tab.closest('[data-hw-panel]'), tab.dataset.hwTab);
    });

    // Deep links: ?city=calgary or ?postal=T2P1J9 (for ads and city pages).
    const params = new URLSearchParams(window.location.search);
    const city = params.get('city');
    if (city && cities.some((o) => o.value === city)) {
      select.value = city;
      show(city);
    } else if (params.get('postal')) {
      postal.value = params.get('postal');
      lookup(postal.value);
    }
  }

  const start = (scope) => scope.querySelectorAll('[data-hw-checker]').forEach(init);
  start(document);
  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();

// Embed badges and citations: copy buttons, and the city picker on the report page.
(() => {
  async function copy(button) {
    const target = document.getElementById(button.dataset.hwCopy);
    if (!target) return;
    const text = 'value' in target ? target.value : target.textContent.trim();
    const label = button.dataset.label || button.textContent;
    button.dataset.label = label;
    const status = button.parentElement.querySelector('[data-hw-copy-status]');
    try {
      await navigator.clipboard.writeText(text);
      button.textContent = button.dataset.copied;
    } catch {
      if ('select' in target) target.select();
      button.textContent = button.dataset.failed;
    }
    if (status) status.textContent = button.textContent;
    clearTimeout(button.hwTimer);
    button.hwTimer = setTimeout(() => (button.textContent = label), 2500);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-hw-copy]');
    if (button) copy(button);
  });

  document.addEventListener('focusin', (event) => {
    if (event.target.matches('.hw-embed textarea')) event.target.select();
  });

  // The report renders one badge; another city's badge is the template filled with that option's data.
  const escapeHtml = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-hw-embed-select]');
    if (!select) return;
    const root = select.closest('[data-hw-embeds]');
    const template = root.querySelector('[data-hw-badge-template]');
    const d = select.selectedOptions[0].dataset;
    const fill = {
      __URL__: d.url,
      __HEADING__: d.heading,
      __VALUE__: d.value,
      __RATING__: d.ratingName,
      __RANK__: d.rank,
      __FOOTER__: root.dataset.footer,
      __ACCENT__: d.accent,
      __TINT__: d.tint,
    };
    const html = template.textContent.trim().replace(/__[A-Z]+__/g, (token) => escapeHtml(fill[token] ?? ''));
    root.querySelector('[data-hw-embed-preview]').innerHTML = html;
    root.querySelector('[data-hw-embed-code]').value = html;
  });
})();
